import { useState, type FormEvent } from "react";
import { Link, useNavigate } from "react-router-dom";
import {
  createUserWithEmailAndPassword,
  sendEmailVerification,
  signInWithEmailAndPassword,
  signInWithPopup
} from "firebase/auth";
import { auth, googleProvider } from "../lib/firebase";
import { useAuth } from "../lib/auth";
import { useGoogleOneTap } from "../lib/googleOneTap";
import { PasswordInput } from "../components/PasswordInput";

type Mode = "signin" | "signup";

export function Login() {
  const nav = useNavigate();
  const { user } = useAuth();
  useGoogleOneTap(!user);
  const [mode, setMode] = useState<Mode>("signin");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const onSubmit = async (e: FormEvent) => {
    e.preventDefault();
    setBusy(true);
    setError(null);
    try {
      if (mode === "signin") {
        await signInWithEmailAndPassword(auth, email, password);
      } else {
        const cred = await createUserWithEmailAndPassword(auth, email, password);
        try {
          await sendEmailVerification(cred.user);
        } catch {
          /* no bloqueamos signup si el envio falla */
        }
      }
      nav("/", { replace: true });
    } catch (err) {
      setError(messageFor(err));
    } finally {
      setBusy(false);
    }
  };

  const onGoogle = async () => {
    setBusy(true);
    setError(null);
    try {
      await signInWithPopup(auth, googleProvider);
      nav("/", { replace: true });
    } catch (err) {
      setError(messageFor(err));
    } finally {
      setBusy(false);
    }
  };

  return (
    <main className="min-h-dvh bg-cream">
      <div className="container-page flex flex-col items-center gap-8">
        <header className="flex flex-col items-center gap-4 text-center">
          <div className="grid h-16 w-16 place-items-center rounded-full bg-leaf-50 ring-1 ring-leaf-100">
            <LeafMark />
          </div>
          <div>
            <p className="text-xs uppercase tracking-[0.2em] text-leaf-600">Fideliza</p>
            <h1 className="mt-2 font-display text-3xl font-semibold text-leaf-950">
              {mode === "signin" ? "Bienvenido" : "Crea tu cuenta"}
            </h1>
            <p className="mt-2 text-sm text-leaf-700">
              {mode === "signin"
                ? "Inicia sesión para ver tus puntos"
                : "Súmate y empieza a juntar tickets"}
            </p>
          </div>
        </header>

        <div className="card flex w-full flex-col gap-5">
          <button type="button" onClick={onGoogle} disabled={busy} className="btn-secondary">
            <GoogleIcon />
            <span>Continuar con Google</span>
          </button>

          <div className="flex items-center gap-3 text-xs uppercase tracking-wider text-leaf-500">
            <span className="h-px flex-1 bg-leaf-100" />
            o con email
            <span className="h-px flex-1 bg-leaf-100" />
          </div>

          <form onSubmit={onSubmit} className="flex flex-col gap-3">
            <input
              type="email"
              inputMode="email"
              autoComplete="email"
              required
              placeholder="tu@email.com"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              className="input"
            />
            <PasswordInput
              autoComplete={mode === "signin" ? "current-password" : "new-password"}
              required
              minLength={6}
              placeholder="Contraseña"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
            />
            {error && (
              <div className="rounded-2xl border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700">
                {error}
              </div>
            )}
            <button type="submit" disabled={busy} className="btn-primary mt-1">
              {busy ? "Procesando…" : mode === "signin" ? "Entrar" : "Crear cuenta"}
            </button>
          </form>
        </div>

        <div className="flex flex-col items-center gap-2">
          <button
            type="button"
            onClick={() => setMode(mode === "signin" ? "signup" : "signin")}
            className="text-sm font-medium text-leaf-700 underline-offset-4 hover:underline"
          >
            {mode === "signin" ? "¿No tienes cuenta? Regístrate" : "¿Ya tienes cuenta? Inicia sesión"}
          </button>
          {mode === "signin" && (
            <Link
              to="/olvide-contrasena"
              className="text-sm text-leaf-600 underline-offset-4 hover:underline"
            >
              Olvidé mi contraseña
            </Link>
          )}
        </div>
      </div>
    </main>
  );
}

function LeafMark() {
  return (
    <svg viewBox="0 0 64 64" className="h-9 w-9" aria-hidden="true">
      <path
        d="M32 12c-10 9-14 18-14 25 0 9 7 15 14 15s14-6 14-15c0-7-4-16-14-25z"
        fill="#618728"
      />
      <path
        d="M32 20c-3 5-3 11 0 17"
        stroke="#ffffff"
        strokeWidth="2.5"
        strokeLinecap="round"
        fill="none"
      />
    </svg>
  );
}

function GoogleIcon() {
  return (
    <svg viewBox="0 0 18 18" className="h-5 w-5" aria-hidden="true">
      <path fill="#4285F4" d="M17.64 9.2c0-.64-.06-1.25-.16-1.84H9v3.48h4.84a4.14 4.14 0 0 1-1.8 2.72v2.26h2.92c1.7-1.57 2.68-3.88 2.68-6.62z" />
      <path fill="#34A853" d="M9 18c2.43 0 4.47-.8 5.96-2.18l-2.92-2.26c-.8.54-1.84.86-3.04.86-2.34 0-4.32-1.58-5.03-3.7H.92v2.32A9 9 0 0 0 9 18z" />
      <path fill="#FBBC05" d="M3.97 10.72A5.41 5.41 0 0 1 3.68 9c0-.6.1-1.18.29-1.72V4.96H.92A9 9 0 0 0 0 9c0 1.45.35 2.82.92 4.04l3.05-2.32z" />
      <path fill="#EA4335" d="M9 3.58c1.32 0 2.5.46 3.44 1.35l2.58-2.58A9 9 0 0 0 9 0 9 9 0 0 0 .92 4.96l3.05 2.32C4.68 5.16 6.66 3.58 9 3.58z" />
    </svg>
  );
}

function messageFor(err: unknown): string {
  const code = (err as { code?: string })?.code ?? "";
  switch (code) {
    case "auth/invalid-credential":
    case "auth/wrong-password":
    case "auth/user-not-found":
      return "Email o contraseña incorrectos.";
    case "auth/email-already-in-use":
      return "Ese email ya tiene cuenta. Inicia sesión.";
    case "auth/weak-password":
      return "La contraseña debe tener al menos 6 caracteres.";
    case "auth/popup-closed-by-user":
      return "Cancelaste el inicio de sesión con Google.";
    case "auth/network-request-failed":
      return "Sin conexión. Revisa tu internet.";
    default:
      return "No pudimos completar la acción. Intenta de nuevo.";
  }
}
